import apiClient from "./axios";
import { ApiResponse } from "../types";

/**
 * The business plan catalogue.
 *
 * Separate from `configApi`'s premium settings: that is the consumer app's
 * per-user, single-tier subscription. This is the business ladder — four
 * tiers, ~70 gated capabilities and a price per term.
 */

/**
 * What a matrix cell can say.
 *
 * `false` is legal for a numeric feature and means "not included", which is a
 * different statement from a cap of `0`. Treating them as the same is how an
 * uncapped tier ends up blocking everything.
 */
export type FeatureValue = boolean | number | string;

export const UNLIMITED = "unlimited";

export interface AdminPlan {
  id: string;
  key: string;
  name: string;
  tagline: string | null;
  monthlyPriceKobo: number;
  annualPriceKobo: number;
  status: "draft" | "published" | "retired";
  isDefault: boolean;
  sort: number;
  /** Every business that has ever been on it. Any of them blocks a delete:
   *  their history points at the plan. */
  subscribers: number;
  /** On it now: trialing, active, past due, or cancelled but paid up.
   *  Absent from an older backend. */
  currentSubscribers?: number;
}

export interface AdminPlanFeature {
  id: string;
  key: string;
  label: string;
  hint: string | null;
  group: string;
  /** Absent from an older backend. */
  sectionId?: string | null;
  valueType: "boolean" | "number" | "enum" | "text";
  unit: string | null;
  enumOptions: string[] | null;
  /**
   * Shown in the apps' comparison against the free plan ("5× Free") rather
   * than as a number. Absent from an older backend.
   */
  relativeToFree?: boolean;
  sort: number;
  /** Removed from the plan builder: on for every business at `ungatedValue`. */
  archived: boolean;
  ungatedValue?: FeatureValue;
}

export interface PlanSection {
  id: string;
  name: string;
  sort: number;
  /** Removed, with the features that were in it. */
  archived: boolean;
  featureCount: number;
}

export interface PlanCatalogue {
  plans: AdminPlan[];
  features: AdminPlanFeature[];
  /** Absent from an older backend, which only knew group names. */
  sections?: PlanSection[];
  /** planKey -> featureKey -> value */
  matrix: Record<string, Record<string, FeatureValue>>;
}

export interface PlanOverride {
  id: string;
  featureKey: string | null;
  value: FeatureValue;
  note: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface PlanSubscriber {
  businessId: string;
  status: string;
  term: string;
  priceKobo: number;
  startedAt: string;
  renewsAt: string | null;
}

export interface PlanEconomicsRow {
  planKey: string;
  planName: string;
  monthlyPriceKobo: number;
  subscribers: number;
  /** List price × subscribers — the projection a price decision is made on. */
  mrrKobo: number;
  /** Money that actually arrived in the window. */
  collectedKobo: number;
  modelMicroUsd: number;
  modelCostKobo: number;
  marginKobo: number;
  modelCostPerBusinessKobo: number;
}

export interface PlanEconomics {
  since: string;
  until: string;
  ngnPerUsd: number;
  /** False when no FX rate is set — every naira cost then reads ₦0. */
  fxConfigured: boolean;
  plans: PlanEconomicsRow[];
  overhead: {
    modelMicroUsd: number;
    modelCostKobo: number;
    note: string;
  };
}

export interface PurposeCostRow {
  purpose: string;
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  microUsd: number;
  /** Per call — the figure an allowance is set against. */
  microUsdPerCall: number;
}


export interface PlanGift {
  id: string;
  businessId: string;
  businessName: string;
  planName: string;
  months: number;
  startsAt: string;
  endsAt: string;
  status: "active" | "expired" | "ended";
  note: string | null;
  endedAt: string | null;
  createdAt: string;
}

export interface PlanCoupon {
  id: string;
  code: string;
  description: string | null;
  kind: "percent" | "amount";
  percentOff: number | null;
  amountOffKobo: number | null;
  planIds: string[];
  terms: string[];
  duration: "once" | "repeating" | "forever";
  durationPayments: number | null;
  maxRedemptions: number | null;
  redemptions: number;
  startsAt: string | null;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
  /** "20% off your first 3 payments". */
  summary: string;
}

export interface CouponInput {
  code: string;
  description?: string;
  kind: "percent" | "amount";
  percentOff?: number;
  amountOffKobo?: number;
  planIds?: string[];
  terms?: string[];
  duration: "once" | "repeating" | "forever";
  durationPayments?: number;
  maxRedemptions?: number;
  startsAt?: string;
  expiresAt?: string;
}

export interface CouponRedemption {
  id: string;
  businessId: string;
  businessName: string;
  paymentsLeft: number | null;
  discountKoboTotal: number;
  createdAt: string;
}

export const plansApi = {
  economics: async (days = 30) => {
    const { data } = await apiClient.get<ApiResponse<PlanEconomics>>(
      `/admin/plans/economics?days=${days}`,
    );
    return data.data;
  },

  purposeCosts: async (days = 30) => {
    const { data } = await apiClient.get<ApiResponse<PurposeCostRow[]>>(
      `/admin/plans/economics/purposes?days=${days}`,
    );
    return data.data;
  },

  getCatalogue: async () => {
    const { data } =
      await apiClient.get<ApiResponse<PlanCatalogue>>("/admin/plans");
    return data.data;
  },

  createPlan: async (input: {
    key: string;
    name: string;
    tagline?: string;
    monthlyPriceKobo: number;
    annualPriceKobo: number;
    sort?: number;
  }) => {
    const { data } = await apiClient.post<ApiResponse<AdminPlan>>(
      "/admin/plans",
      input,
    );
    return data.data;
  },

  updatePlan: async (
    planId: string,
    input: Partial<{
      name: string;
      tagline: string;
      monthlyPriceKobo: number;
      annualPriceKobo: number;
      status: "draft" | "published" | "retired";
      isDefault: boolean;
      sort: number;
    }>,
  ) => {
    const { data } = await apiClient.patch<ApiResponse<AdminPlan>>(
      `/admin/plans/${planId}`,
      input,
    );
    return data.data;
  },

  deletePlan: async (planId: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/admin/plans/${planId}`,
    );
    return data.data;
  },

  /** Set one cell. The server validates the value against the feature's type. */
  setFeature: async (planId: string, featureKey: string, value: FeatureValue) => {
    const { data } = await apiClient.put<
      ApiResponse<{ planKey: string; featureKey: string; value: FeatureValue }>
    >(`/admin/plans/${planId}/features/${featureKey}`, { value });
    return data.data;
  },

  createFeature: async (input: {
    key: string;
    label: string;
    hint?: string;
    sectionId?: string;
    group?: string;
    valueType: "boolean" | "number" | "enum" | "text";
    unit?: string;
    enumOptions?: string;
    relativeToFree?: boolean;
    sort?: number;
  }) => {
    const { data } = await apiClient.post<ApiResponse<AdminPlanFeature>>(
      "/admin/plans/features",
      input,
    );
    return data.data;
  },

  updateFeature: async (
    featureId: string,
    input: Partial<{
      label: string;
      hint: string;
      group: string;
      unit: string;
      enumOptions: string;
      relativeToFree: boolean;
      sort: number;
      archived: boolean;
      sectionId: string;
    }>,
  ) => {
    const { data } = await apiClient.patch<ApiResponse<AdminPlanFeature>>(
      `/admin/plans/features/${featureId}`,
      input,
    );
    return data.data;
  },

  createSection: async (name: string) => {
    const { data } = await apiClient.post<ApiResponse<PlanSection>>(
      "/admin/plans/sections",
      { name },
    );
    return data.data;
  },

  /** Rename or reorder. A rename reaches every feature in the section. */
  updateSection: async (sectionId: string, input: { name?: string; sort?: number }) => {
    const { data } = await apiClient.patch<ApiResponse<PlanSection>>(
      `/admin/plans/sections/${sectionId}`,
      input,
    );
    return data.data;
  },

  /** Removes the section and every feature in it: on for everybody. */
  removeSection: async (sectionId: string) => {
    const { data } = await apiClient.post<
      ApiResponse<{ id: string; featuresRemoved: number }>
    >(`/admin/plans/sections/${sectionId}/remove`);
    return data.data;
  },

  restoreSection: async (sectionId: string) => {
    const { data } = await apiClient.post<
      ApiResponse<{ id: string; featuresRestored: number }>
    >(`/admin/plans/sections/${sectionId}/restore`);
    return data.data;
  },

  gifts: async (status?: string) => {
    const { data } = await apiClient.get<ApiResponse<PlanGift[]>>(
      "/admin/plans/gifts",
      { params: status ? { status } : {} },
    );
    return data.data;
  },

  /** Give a business a plan for some months. */
  gift: async (input: { businessId: string; planId: string; months: number; note?: string }) => {
    const { data } = await apiClient.post<ApiResponse<PlanGift>>("/admin/plans/gifts", input);
    return data.data;
  },

  endGift: async (giftId: string) => {
    const { data } = await apiClient.post<ApiResponse<PlanGift>>(`/admin/plans/gifts/${giftId}/end`);
    return data.data;
  },

  coupons: async () => {
    const { data } = await apiClient.get<ApiResponse<PlanCoupon[]>>("/admin/plans/coupons");
    return data.data;
  },

  createCoupon: async (input: CouponInput) => {
    const { data } = await apiClient.post<ApiResponse<PlanCoupon>>("/admin/plans/coupons", input);
    return data.data;
  },

  updateCoupon: async (
    couponId: string,
    input: Partial<{ description: string; active: boolean; maxRedemptions: number | null; expiresAt: string | null }>,
  ) => {
    const { data } = await apiClient.patch<ApiResponse<PlanCoupon>>(`/admin/plans/coupons/${couponId}`, input);
    return data.data;
  },

  redemptions: async (couponId: string) => {
    const { data } = await apiClient.get<ApiResponse<CouponRedemption[]>>(
      `/admin/plans/coupons/${couponId}/redemptions`,
    );
    return data.data;
  },

  subscribers: async (planId: string) => {
    const { data } = await apiClient.get<ApiResponse<PlanSubscriber[]>>(
      `/admin/plans/${planId}/subscribers`,
    );
    return data.data;
  },

  listOverrides: async (businessId: string) => {
    const { data } = await apiClient.get<ApiResponse<PlanOverride[]>>(
      `/admin/plans/businesses/${businessId}/overrides`,
    );
    return data.data;
  },

  setOverride: async (
    businessId: string,
    input: {
      featureKey: string;
      value: FeatureValue;
      note: string;
      expiresAt?: string;
    },
  ) => {
    const { data } = await apiClient.put<
      ApiResponse<{ featureKey: string; value: FeatureValue }>
    >(`/admin/plans/businesses/${businessId}/overrides`, input);
    return data.data;
  },

  removeOverride: async (businessId: string, featureKey: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ removed: boolean }>>(
      `/admin/plans/businesses/${businessId}/overrides/${featureKey}`,
    );
    return data.data;
  },

  assignPlan: async (
    businessId: string,
    input: {
      planId: string;
      term: "monthly" | "quarterly" | "annual";
      priceKobo?: number;
    },
  ) => {
    const { data } = await apiClient.put<
      ApiResponse<{ businessId: string; planKey: string }>
    >(`/admin/plans/businesses/${businessId}/plan`, input);
    return data.data;
  },
};

/** Kobo in, "₦14,500" out. */
export function formatKobo(kobo: number): string {
  return `₦${(kobo / 100).toLocaleString("en-NG")}`;
}

/** How a cell reads in the matrix when it is not being edited. */
export function describeValue(
  value: FeatureValue | undefined,
  feature: AdminPlanFeature,
): string {
  if (value === undefined) return "—";
  if (value === false) return "—";
  if (value === true) return "Included";
  if (value === UNLIMITED) return "Unlimited";
  if (typeof value === "number") {
    return feature.unit ? `${value.toLocaleString()} ${feature.unit}` : String(value);
  }
  return String(value);
}

/**
 * What the apps show for a value against the free plan's: "5× Free", or
 * "Base" for the free plan's own amount. Null when the feature is not shown
 * that way, or there is no free amount to measure against. The same rule as
 * the business app's `timesFree`.
 */
export function timesFree(
  value: FeatureValue | undefined,
  base: FeatureValue | undefined,
  feature: AdminPlanFeature,
): string | null {
  if (!feature.relativeToFree) return null;
  if (typeof value !== "number" || typeof base !== "number" || base <= 0) return null;
  const ratio = value / base;
  if (Math.abs(ratio - 1) < 0.05) return "Base";
  const shown = ratio >= 10 ? Math.round(ratio) : Math.round(ratio * 10) / 10;
  return `${shown}× Free`;
}

/** The free plan: the one keyed `free`, or failing that the one that costs nothing. */
export function freePlanOf<P extends { key: string; monthlyPriceKobo: number }>(
  plans: P[],
): P | undefined {
  return plans.find((p) => p.key === "free") ?? plans.find((p) => p.monthlyPriceKobo === 0);
}

/** Micro-USD in, "$0.0164" out — the scale a single call lives at. */
export function formatMicroUsd(micro: number): string {
  return `$${(micro / 1_000_000).toFixed(4)}`;
}
